import { useEffect, useRef, useMemo, useContext } from "react";
import { useOneTimePassContext } from "@/features/accounts/payment/scraping/contexts/one-time-pass-context";
//import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { AggregateStatusService } from "@/features/accounts/finance/services/aggregate-status-service";
import { CrecaAggregateStatusService } from "@/features/accounts/creca/services/crecaaggregate-status-service";
import { ScrapingAggregateStatusService } from "@/features/accounts/payment/scraping/services/scrapingaggregate-status-service";
import { useAccountPollingContext } from "@/features/accounts/shared/contexts/account-polling-context";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";

export const useAggregationPolling = (onBankPollingCompleted?: () => void) => {
  const {
    resetOtpContext,
    setOneTimePassList,
    setSessionKey,
    setCount,
    setOtpInfo,
  } = useOneTimePassContext();
  const {
    pollingInfos,
    removePollingInfo,
  } = useAccountPollingContext();
  const {
    openHomeModal,
    setOpenHomeModal
  } = useModalContext();
  const loadingCtx = useContext(LoadingModalContext);
  const pollingRef = useRef<number | null>(null);
  const startTimeMapRef = useRef<Map<string, number>>(new Map());

  // PollingContextにあるprocessIdを使用する
  const processingAccounts = useMemo(() => {
    return pollingInfos.map(info => ({
      aggProcessId: info.processId, // PollingContextのIDを使用
      accountGroupId: info.accountGroupId,
      mlinkAccountId: info.mlinkAccountId,
      mlinkId: info.mlinkId,
      passbookType: info.passbookType,
      scrapeType: info.scrapeType,
    }));
  }, [pollingInfos]);

  useEffect(() => {

    // 新規の処理対象があれば開始時間を記録
    processingAccounts.forEach(acc => {
      if (acc.aggProcessId && !startTimeMapRef.current.has(acc.aggProcessId)) {
        startTimeMapRef.current.set(acc.aggProcessId, Date.now());
      }
    });

    // 処理対象から外れたIDはMapから削除
    // 逆にprocessingAccountsにないIDは削除する
    const currentProcessIds = new Set(processingAccounts.map(acc => acc.aggProcessId).filter(Boolean) as string[]);
    for (const key of startTimeMapRef.current.keys()) {
      if (!currentProcessIds.has(key)) {
        startTimeMapRef.current.delete(key);
      }
    }

    // 処理中の口座がない場合、ポーリング停止
    if (processingAccounts.length === 0) {
      if (pollingRef.current) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }


    // ポーリング関数
    const poll = async () => {
      const now = Date.now();
      const ONE_HOUR = 60 * 60 * 1000; // 1時間


      for (const account of processingAccounts) {
        // aggProcessIdは必ず存在するはずだが、型定義上チェック
        if (!account.aggProcessId) continue;
        const processId = account.aggProcessId; // 変数に退避

        // 開始時間　取得できない場合はタイムアウトより前の時間を格納
        const startTime = startTimeMapRef.current.get(processId) ?? (now - (ONE_HOUR * 2));
        if ((now - startTime > ONE_HOUR)) {
            // タイムアウト時はPollingContextから削除
            removePollingInfo(processId);
            continue;
        }

        const process_started_at = new Date(startTime).toISOString()

        try {
          // サービスを選択
          let service: AggregateStatusService | CrecaAggregateStatusService | ScrapingAggregateStatusService | null = null;
          let res;
          if (account.passbookType === "01") {
              service = new AggregateStatusService();
              res = await service.getAggregateStatus({
                process_id: processId,
                account_group_id: account.accountGroupId,
                finance_account_id: account.mlinkAccountId,
                finance_id: account.mlinkId,
                process_started_at: process_started_at
              });
          } else if (account.passbookType === "19659" || account.passbookType === "19660") {
              service = new CrecaAggregateStatusService();
              res = await service.getAggregateStatus({
                process_id: processId,
                account_group_id: account.accountGroupId,
                creca_account_id: account.mlinkAccountId,
                process_started_at: process_started_at
              });
          } else if ((account.passbookType === "03" || account.passbookType === "10") && account.scrapeType === 2) {
            service = new ScrapingAggregateStatusService();
            
            res = await service.getAggregateStatus({
              process_id: processId,
                payment_account_id: account.mlinkAccountId,
                payment_id: account.mlinkId,
                process_started_at: process_started_at
            });
            // サーバー版スクレイピング: ステータスによるワンタイムパス制御
            if (res.status === 1 && res.otpinfo_list && res.otpinfo_list.length > 0) {
              // 全フィールドが空文字の要素を除外して保存
              const filteredOtpList = res.otpinfo_list.filter(
                (otp: { type: string; key: string; src: string | null; message: string; title: string }) =>
                  (otp.type !== null && otp.type !== "") ||
                  (otp.key !== null && otp.key !== "") ||
                  (otp.src !== null && otp.src !== "") ||
                  (otp.message !== null && otp.message !== "") ||
                  (otp.title !== null && otp.title !== "")
              );
              if (filteredOtpList.length > 0) {
                setCount(0);       // 前回ラウンドのカウントをリセット
                setOtpInfo(undefined); // 前回ラウンドのOTP情報をリセット
                setSessionKey(res.session_key);
                setOneTimePassList(filteredOtpList);
                removePollingInfo(processId);                // openHomeModalがすでに"UpdateTradesOtp"の場合もuseEffectが再実行されないため、
                // ここで直接isLoading=falseにして操作を有効化する
                loadingCtx.setIsLoading(false);                setOpenHomeModal("UpdateTradesOtp");
              }
            }
          } else {
            // API版及びカラーミーはポーリング対象外のためserviceはnull
            continue;
          }

          console.warn("Polling result for account:", account.mlinkAccountId, res);
          // ステータスが変わっていたらPollingContextから削除
          // TODO: マジックナンバーは避ける，ステータスの意味を定数化する
          if (res.status !== 0
              && res.status !== 1) {
            resetOtpContext();
            removePollingInfo(processId);
            if (openHomeModal === "UpdateTrades" || openHomeModal === "UpdateTradesOtp") {
              setOpenHomeModal(undefined);
            }
            onBankPollingCompleted?.();
          }
        } catch (error) {
          console.error("Polling error for account:", account.mlinkAccountId, error);
          // エラー発生時はPollingContextから削除
          resetOtpContext();
          removePollingInfo(processId);
          if (openHomeModal === "UpdateTrades" || openHomeModal === "UpdateTradesOtp") {
            setOpenHomeModal(undefined);
          }
        }
      }
    };

    // 初回実行
    if (!pollingRef.current) {
        // 5秒間隔でポーリング
        pollingRef.current = window.setInterval(poll, 5000);
    }
    
    // クリーンアップ
    return () => {
      if (pollingRef.current) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [processingAccounts, removePollingInfo]);
};
